import * as React from 'react';
import { useState } from 'react';
import { SectionCard, Select } from '../../../components/forms';
import { AsyncConfirm, label, ProductionApi, reason, RemoveButton, useInflight } from './parts';

/* Gear reservations — reserve and release each save on their own. */

const assetLabel = (g: any) => {
    const extra = [g.serialNumber ?? label(g.category)];
    if (g.status && g.status !== 'available') extra.push(label(g.status).toLowerCase());
    else if (Number(g.activeAssignmentCount) > 0) extra.push(`out on ${Number(g.activeAssignmentCount)}`);
    return `${g.name} (${extra.filter(Boolean).join(', ')})`;
};

export default function GearSection({
    api,
    activationId,
    gear,
    assets,
    assetsFailed,
    refresh,
    toast,
}: {
    api: ProductionApi;
    activationId: number;
    gear: any[];
    assets: any[];
    assetsFailed: boolean;
    refresh: () => Promise<void>;
    toast: (text: string) => void;
}) {
    const { busy, run } = useInflight();
    const [pick, setPick] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [removing, setRemoving] = useState<any | null>(null);
    const [removeError, setRemoveError] = useState<string | null>(null);

    const available = assets.filter((a) => !gear.some((g) => +g.gearAssetId === +a.id));

    const reserve = async () => {
        if (!pick) return;
        const asset = assets.find((a) => +a.id === +pick);
        setError(null);
        const r = await run('add', () => api.addGearAssignment(activationId, { gearAssetId: +pick }));
        if (r.ok) {
            setPick('');
            await refresh();
            toast(`${asset?.name ?? 'Gear'} reserved.`);
        } else if (!('skipped' in r && r.skipped)) {
            /* keep the pick so a retry is one click */
            setError(reason(r.error, `Couldn’t reserve ${asset?.name ?? 'that'} — try again.`));
        }
    };

    const remove = async () => {
        if (!removing) return;
        const g = removing;
        setRemoveError(null);
        const r = await run(`remove-${g.id}`, () => api.removeGearAssignment(+g.id));
        if (r.ok) {
            setRemoving(null);
            await refresh();
            toast(`${g.assetName ?? 'Gear'} released.`);
        } else if (!('skipped' in r && r.skipped)) {
            setRemoveError(reason(r.error, `Couldn’t release ${g.assetName ?? 'it'} — try again.`));
        }
    };

    return (
        <SectionCard title="Gear" note={gear.length ? String(gear.length) : undefined}>
            {gear.length === 0 ? (
                <p className="quiet-empty">No gear reserved yet.</p>
            ) : (
                gear.map((g) => (
                    <div className="trow2 prd-row" key={g.id}>
                        <span className="prd-main">
                            <span className="nm2">{g.assetName ?? g.name}</span>
                            <span className="sub2">
                                {[[g.brand, g.model].filter(Boolean).join(' '), g.serialNumber].filter(Boolean).join(' · ')}
                            </span>
                        </span>
                        <span className="prd-ctl">
                            {g.status && <span className="prd-tag">{label(g.status)}</span>}
                            <RemoveButton
                                label={`Release ${g.assetName ?? 'gear'} from this event`}
                                disabled={!!busy[`remove-${g.id}`]}
                                onClick={() => {
                                    setRemoveError(null);
                                    setRemoving(g);
                                }}
                            />
                        </span>
                    </div>
                ))
            )}
            {assetsFailed ? (
                <p className="field-e">Couldn’t load the gear list, so nothing can be reserved. Reload to try again.</p>
            ) : (
                <div className="prd-add">
                    <span className="prd-grow">
                        <Select aria-label="Gear to reserve" value={pick} disabled={!!busy.add} onChange={(e) => setPick(e.target.value)}>
                            <option value="">{available.length ? 'Reserve gear…' : 'All gear is on this event'}</option>
                            {available.map((a) => (
                                <option key={a.id} value={a.id}>
                                    {assetLabel(a)}
                                </option>
                            ))}
                        </Select>
                    </span>
                    <button type="button" className="tb-new" disabled={!pick || !!busy.add} onClick={reserve}>
                        {busy.add ? 'Reserving…' : 'Reserve'}
                    </button>
                </div>
            )}
            {error && (
                <p className="field-e" role="alert">
                    {error}
                </p>
            )}

            <AsyncConfirm
                open={!!removing}
                title={`Release ${removing?.assetName ?? 'gear'}?`}
                actionLabel="Release"
                busyLabel="Releasing…"
                busy={!!(removing && busy[`remove-${removing.id}`])}
                error={removeError}
                danger
                onConfirm={remove}
                onClose={() => setRemoving(null)}
            >
                <p>The reservation for this event is deleted{removing?.status && removing.status !== 'reserved' ? `, including its ${label(removing.status).toLowerCase()} status` : ''}. The item stays in Gear.</p>
            </AsyncConfirm>
        </SectionCard>
    );
}
